import {
  getValidValue,
  toRaphaelColor,
  isIE,
  extend2,
  pluck,
  pluckNumber,
  hasSVG
} from '@fusioncharts/core/src/lib';
import ScatterBase from '@fusioncharts/charts/src/chart/_internal/scatterbase';
import SelectScatterDataset from '../../dataset/selectscatter';
import { getDep } from '@fusioncharts/core/src/dependency-manager';
import { submitData } from '../_internal/editable-charts';
import raphaelShapesButton from '@fusioncharts/core/src/_internal/redraphael/redraphael-shapes/redraphael-shapes.button';

let Raphael = getDep('redraphael', 'plugin'),
  UNDEF,
  COMMA = ',',
  configStr = 'config',
  TRACKER_FILL = 'rgba(192,192,192,' + (isIE ? 0.002 : 0.000001) + ')',
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
  ROUND = 'round',
  POINTER = 'pointer',
  SELECTSCATTER = 'selectScatter',
  resizeInnerSymbolColor = '#999999',
  resizeOuterSymbolColor = '#777777',
  NULLSTR = 'null',
  closeButtonRadius = 6,
  trackerRadius = 12,
  deg2rad = Math.PI / 180,
  checkObjectLength = (item) => {
    return Object.keys(item).length;
  },
  getArcPath = function (endX, endY, rX, rY, isClockWise, isLargeArc) {
    return ['A', rX, rY, 0, isLargeArc, isClockWise, endX, endY];
  },
  M = 'M',
  L = 'L',
  Z = 'Z',
  t = 't';

// Resolve dependency to draw button shape
raphaelShapesButton(Raphael);

function createSelectionBox (event) {
  var chart = event.chart,
    paper = chart.getFromEnv('paper'),
    chartConfig = chart.config,
    yAxis = chart.getChildren('yAxis')[0],
    xAxis = chart.getChildren('xAxis')[0],
    x = event.selectionLeft,
    y = event.selectionTop,
    width = event.selectionWidth,
    height = event.selectionHeight,
    x2 = x + width,
    y2 = y + height,
    TRACKER_WIDTH = 12,
    TRACKER_HALF_WIDTH = TRACKER_WIDTH * 0.5,
    cornerSymbolRadius = 15,
    isSmall = width > cornerSymbolRadius &&
                height > cornerSymbolRadius,
    selectEleObj = {
      resizeEleRadius: cornerSymbolRadius,
      canvasTop: chartConfig.canvasTop,
      canvasRight: chartConfig.canvasLeft + chartConfig.canvasWidth,
      canvasLeft: chartConfig.canvasLeft,
      canvasBottom: chartConfig.canvasTop + chartConfig.canvasHeight
    },
    selectEleArr = chartConfig._selectEleArr || (chartConfig._selectEleArr = []),
    selectBoxG;

  // var TRACKER_FILL = 'rgba(255,0,0,0.3)';

  selectEleObj.index = selectEleArr.length;
  selectEleObj.id = 'SELECT_' + selectEleObj.index;

  selectEleObj.selectBoxG = selectBoxG = paper.group('selection-box',
    chart.getChildContainer('trackerGroup')).toFront();

  // Drawing the main box element
  selectEleObj.selectBoxTracker = paper.rect(x, y, width, height, selectBoxG)
    .attr({
      'stroke-width': 1,
      stroke: toRaphaelColor(chartConfig.selectBorderColor),
      fill: chartConfig.selectFillColor
    })
    .css({
      cursor: 'move'
    });
  selectEleObj.selectBoxTracker.node._isTrackerElem = true;

  selectEleObj.selectBoxTracker.data(configStr, {
    position: 6, // MOVE
    selectEleObj: selectEleObj,
    chart: chart,
    xChange: true,
    yChange: true
  });

  // Draw top tracker element
  selectEleObj.topTracker = paper.rect(x, y -
            TRACKER_HALF_WIDTH, width, TRACKER_WIDTH, selectBoxG)
    .attr({
      'stroke-width': 0,
      fill: TRACKER_FILL
    })
    .css('cursor', (hasSVG && 'ns-resize') || 'n-resize');
  selectEleObj.topTracker.node._isTrackerElem = true;
  selectEleObj.topTracker.data(configStr, {
    position: 1, // TOP
    selectEleObj: selectEleObj,
    yChange: true,
    chart: chart
  });

  // Draw right tracker element
  selectEleObj.rightTracker = paper.rect(x + width -
            TRACKER_HALF_WIDTH, y, TRACKER_WIDTH, height, selectBoxG)
    .attr({
      'stroke-width': 0,
      fill: TRACKER_FILL
    })
    .css('cursor', (hasSVG && 'ew-resize') || 'w-resize');
  selectEleObj.rightTracker.node._isTrackerElem = true;
  selectEleObj.rightTracker.data(configStr, {
    position: 2, // RIGHT
    chart: chart,
    selectEleObj: selectEleObj,
    xChange: true
  });

  // Draw bottom tracker element
  selectEleObj.bottomTracker = paper.rect(x, y + height -
            TRACKER_HALF_WIDTH, width, TRACKER_WIDTH, selectBoxG)
    .attr({
      'stroke-width': 0,
      fill: TRACKER_FILL
    })
    .css('cursor', (hasSVG && 'ns-resize') || 'n-resize');
  selectEleObj.bottomTracker.node._isTrackerElem = true;
  selectEleObj.bottomTracker.data(configStr, {
    position: 3, // BOTTOM
    chart: chart,
    selectEleObj: selectEleObj,
    yChange: true
  });

  // Draw left tracker element
  selectEleObj.leftTracker = paper.rect(x - TRACKER_HALF_WIDTH, y,
    TRACKER_WIDTH, height, selectBoxG)
    .attr({
      'stroke-width': 0,
      fill: TRACKER_FILL
    })
    .css('cursor', (hasSVG && 'ew-resize') || 'e-resize');
  selectEleObj.leftTracker.node._isTrackerElem = true;
  selectEleObj.leftTracker.data(configStr, {
    position: 4, // LEFT
    chart: chart,
    selectEleObj: selectEleObj,
    xChange: true
  });

  selectEleObj.cornerInnerSymbol = paper.symbol('resizeIcon', 0, 0,
    cornerSymbolRadius, selectBoxG)
    .attr({
      transform: t + x2 + COMMA + y2,
      'stroke-width': 1,
      visibility: isSmall ? VISIBLE : HIDDEN,
      stroke: resizeInnerSymbolColor
    });
  selectEleObj.cornerInnerSymbol.node._isTrackerElem = true;

  selectEleObj.cornerOuterSymbol = paper.symbol('resizeIcon', 0, 0, -cornerSymbolRadius * 0.8, selectBoxG)
    .attr({
      transform: t + x2 + COMMA + y2,
      strokeWidth: 1,
      visibility: !isSmall ? VISIBLE : HIDDEN,
      stroke: resizeOuterSymbolColor
    });
  selectEleObj.cornerOuterSymbol.node._isTrackerElem = true;

  selectEleObj.resizeTracker = paper.circle(x2, y2, trackerRadius, selectBoxG)
    .attr({
      'stroke-width': 1,
      stroke: TRACKER_FILL,
      fill: TRACKER_FILL
    })
    .css('cursor', (hasSVG && 'nwse-resize') || 'nw-resize');
  selectEleObj.resizeTracker.node._isTrackerElem = true;
  selectEleObj.resizeTracker.data(configStr, {
    position: 5, // Corner
    chart: chart,
    selectEleObj: selectEleObj,
    yChange: true,
    xChange: true
  });

  selectEleObj.closeButton = paper.symbol('closeIcon', 0, 0, closeButtonRadius, selectBoxG)
    .attr({
      transform: 't' + x2 + COMMA + y,
      'stroke-width': 2,
      stroke: chartConfig.selectionCancelButtonBorderColor,
      fill: chartConfig.selectionCancelButtonFillColor,
      'stroke-linecap': ROUND,
      'stroke-linejoin': ROUND
    })
    .css({
      cursor: POINTER,
      _cursor: 'hand'
    })
    .on('fc-click', function () { // Delete the selection
      chart.deleteSelection(this, chart);
    });
  selectEleObj.closeButton.node._isTrackerElem = true;
  selectEleObj.closeButton.data(configStr, {
    chart: chart,
    index: selectEleObj.index
  });

  // Previously wrong calculations were done. Fixed it.
  selectEleObj.startX = xAxis.getValue(x);
  selectEleObj.startY = yAxis.getValue(y);
  selectEleObj.endX = xAxis.getValue(x2);
  selectEleObj.endY = yAxis.getValue(y2);
  selectEleObj.isVisible = true;

  selectEleArr.push(selectEleObj);
  chart.bindDragEvent(selectEleObj);
}

function deleteSelection (ele, chart) {
  var index = ele.data(configStr).index, // eslint-disable-line good-practices/no-single-usage-variable
    selectEleArr = chart.config._selectEleArr,
    selectEleObj,
    selectEleItem,
    xAxis = chart.getChildren('xAxis')[0],
    yAxis = chart.getChildren('yAxis')[0],
    items,
    bBox,
    eventArgs;
  selectEleObj = selectEleArr.find(function (item) {
    return item.index === index;
  });
  bBox = selectEleObj.selectBoxTracker.getBBox();
  eventArgs = { // eslint-disable-line good-practices/no-single-usage-variable
    selectionLeft: bBox.x,
    selectionTop: bBox.y,
    selectionWidth: bBox.width,
    selectionHeight: bBox.height,
    startXValue: xAxis.getValue(bBox.x, 1),
    startYValue: yAxis.getValue(bBox.y, 1),
    endXValue: xAxis.getValue(bBox.x + bBox.width, 1),
    endYValue: yAxis.getValue(bBox.y + bBox.height, 1),
    data: chart.getCollatedData(),
    id: selectEleObj.id
  };

  for (items in selectEleObj) {
    if (selectEleObj.hasOwnProperty(items)) {
      selectEleItem = selectEleObj[items];
      selectEleItem.remove && selectEleItem.remove();
      delete selectEleObj[items];
    }
  }
  // splice out the last removed slection box
  selectEleArr = selectEleArr.filter(checkObjectLength);
  /**
   * This event is raised when the selection of a SelectScatter chart is removed. This happens when one
   * clicks the close button on a selection that one has made on the chart.
   *
   * @event FusionCharts#selectionRemoved
   * @group chart-powercharts:selectscatter
   *
   * @param {Object} data - This returns the subset of data that was selected.
   */
  chart.fireChartInstanceEvent('selectionRemoved', eventArgs);
}

Raphael.addSymbol({
  resizeIcon: function (xVal, yVal, rad) {
    var
      radius = rad,
      x = xVal,
      y = yVal,
      LINE_GAP = pluckNumber(radius, 15) / 3,
      LINE_DIS = 3,
      paths = [],
      i;

    if (LINE_GAP < 0) {
      LINE_GAP = -LINE_GAP;
      radius = -radius;
      x += radius - LINE_GAP / 2;
      y += radius - LINE_GAP / 2;
    }

    for (i = 3; i > 0; i -= 1) {
      paths.push(M, x - LINE_GAP * i, y - LINE_DIS,
        L, x - LINE_DIS, y - LINE_GAP * i);
    }
    return paths;
  },
  closeIcon: function (x, y, r) {
    var
      icoX = x,
      icoY = y,
      rad = r * 1.3,
      startAngle = 43 * deg2rad,
      // to prevent cos and sin of start and end from becoming
      // equal on 360 arcs
      endAngle = 48 * deg2rad,
      startX = icoX + rad * Math.cos(startAngle), // eslint-disable-line good-practices/no-single-usage-variable
      startY = icoY + rad * Math.sin(startAngle), // eslint-disable-line good-practices/no-single-usage-variable
      endX = icoX + rad * Math.cos(endAngle), // eslint-disable-line good-practices/no-single-usage-variable
      endY = icoY + rad * Math.sin(endAngle), // eslint-disable-line good-practices/no-single-usage-variable
      paths,
      r1 = 0.71 * (r - 2), // (r - 2) * 0.5,
      r2 = 0.71 * (r - 2), // (r - 2) * 0.87,

      arcPath = getArcPath(endX, endY, rad, rad, 0, 1); // eslint-disable-line good-practices/no-single-usage-variable

    paths = [M, startX, startY];
    paths = paths.concat(arcPath);
    paths = paths.concat([
      M, x + r1, y - r2,
      L, x - r1, y + r2,
      M, x - r1, y - r2,
      L, x + r1, y + r2
    ]);

    return paths;
  },
  configureIcon: function (x, y, rVal) {
    var k = 0.5,
      r = rVal - 1,
      l = 0.25,
      r1 = r * 0.71,
      r2 = (r + 2) * 0.71,
      x1 = x - r,
      y1 = y - r,
      x2 = x + r,
      y2 = y + r,
      x3 = x + k,
      y3 = y + k,
      x4 = x - k,
      y4 = y - k,

      x5 = x1 - 2,
      y5 = y1 - 2,
      x6 = x2 + 2,
      y6 = y2 + 2,
      x7 = x + r1,
      y7 = y + r1,
      x8 = x - r1,
      y8 = y - r1,
      x9 = x + r2,
      y9 = y + r2,
      x10 = x - r2,
      y10 = y - r2,
      paths;
    // eslint-disable-next-line good-practices/no-single-usage-variable
    paths = [M, x1, y3, L, x5, y3, x5, y4, x1, y4,
      x8 - l, y8 + l, x10 - l, y10 + l, x10 + l, y10 - l, x8 + l, y8 - l,
      x4, y1, x4, y5, x3, y5, x3, y1,
      x7 - l, y8 - l, x9 - l, y10 - l, x9 + l, y10 + l, x7 + l, y8 + l,
      x2, y4, x6, y4, x6, y3, x2, y3,
      x7 + l, y7 - l, x9 + l, y9 - l, x9 - l, y9 + l, x7 - l, y7 + l,
      x3, y2, x3, y6, x4, y6, x4, y2,
      x8 + l, y7 + l, x10 + l, y9 + l, x10 - l, y9 - l, x8 - l, y7 - l, Z
    ];
    return paths;
  },
  axisIcon: function (x, y, rVal) {
    var
      r = rVal - 1,
      r1 = r * 0.33,
      r2 = r / 2,
      x1 = x - r,
      y1 = y - r,
      x2 = x + r2,
      y2 = y + r,
      x3 = x - r2,
      y3 = y + r1,
      y4 = y - r1,
      paths;
    // eslint-disable-next-line good-practices/no-single-usage-variable
    paths = [M, x1, y1, L, x2, y1, x2, y2, x1, y2, M, x3, y3, L, x2, y3,
      M, x3, y4, L, x2, y4
    ];
    return paths;
  },
  loggerIcon: function (xVal, yVal, rVal) {
    var
      r = rVal - 1,
      y = yVal - r,
      x = xVal - r,
      r2 = r * 2, // eslint-disable-line good-practices/no-single-usage-variable
      x1 = x + r2,
      x2 = x + 2,
      x3 = x1 - 2,
      y1 = y + 2,
      y2 = y1 + r,
      y3 = y2 + 2,
      paths;
    // eslint-disable-next-line good-practices/no-single-usage-variable
    paths = [M, x, y, L, x1, y, x1, y1, x3, y1, x3, y2, x1, y2, x1, y3,
      x, y3, x, y2, x2, y2, x2, y1, x, y1, x, y
    ];
    return paths;
  }
});

class SelectScatter extends ScatterBase {
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'SelectScatter';
  }

  /**
   * Parse the chart attributes and store in chart's config
   * @param {Object} dataObj User input json
   */
  parseChartAttr (dataObj) {
    super.parseChartAttr(dataObj);
    this.config.formBtnTitle = pluck(
      dataObj.chart.submittext,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE formbtntitle parsing after two releases from 3.13.0
      dataObj.chart.formbtntitle,
      'Submit'
    );
    this.config.restoreBtnTitle = pluck(
      dataObj.chart.restoretext,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE restorebtntitle parsing after two releases from 3.13.0
      dataObj.chart.restorebtntitle,
      'Restore'
    );
  }

  constructor () {
    super();
    this.isXY = true;

    this.defaultZeroPlaneHighlighted = false;
    this.eiMethods = {
      // eslint-disable-next-line good-practices/no-function-dependency
      getData: function (format) {
        var apiInstance = this.apiInstance;
        return apiInstance && apiInstance.getData(format);
      },
      // eslint-disable-next-line good-practices/no-function-dependency
      restoreData: function () {
        var apiInstance = this.apiInstance;
        return apiInstance && apiInstance.restoreData();
      },
      // eslint-disable-next-line good-practices/no-function-dependency
      submitData: function () {
        var apiInstance = this.apiInstance;
        return apiInstance && apiInstance.submitData();
      }
    };
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'SelectScatter';
  }

  /**
   * Configures and parses chart attributes.
   *
   * @param {any} dataObj json data
   * @memberof SelectScatter
   */
  configureAttributes (dataObj) {
    var iapi = this,
      chartConfig = iapi.getFromEnv('chart').config,
      chartAttr = iapi.getFromEnv('dataSource').chart || {};

    chartConfig.formAction = getValidValue(chartAttr.formaction);
    chartConfig.enableSubmit = pluckNumber(
      chartAttr.enablesubmit,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE showformbtn parsing after two releases from 3.13.0
      chartAttr.showformbtn,
      1
    ) && chartConfig.formAction;
    chartConfig.enableRestore = pluckNumber(
      chartAttr.enablerestore,
      /**
       * @deprecated since 3.13.0.
       */
      // TODO: REMOVE showrestorebtn parsing after two releases from 3.13.0
      chartAttr.showrestorebtn,
      1
    );
    super.configureAttributes(dataObj);
  }

  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.hasLegend = true;
    config.defaultDatasetType = SELECTSCATTER;
    config.allowreversexaxis = true;
    config.enablemousetracking = true;
  }

  attachMenuButtons () {
    super.attachMenuButtons();
    var iapi = this,
      chartConfig = iapi.getFromEnv('chartConfig'),
      toolbar = iapi.getFromEnv('toolbar'),
      hamburgerMenu = toolbar.getChild(`hamburgerMenu-${toolbar.getId()}-${iapi.getId()}-0`), // eslint-disable-line good-practices/no-single-usage-variable
      list = [],
      tempObj;

    chartConfig.enableRestore && list.push({
      name: chartConfig.restoreBtnTitle,
      handler: function () {
        iapi.restoreData();
      },
      action: 'click'
    });

    if (chartConfig.enableSubmit) {
      tempObj = {
        name: chartConfig.formBtnTitle,
        handler: function () {
          submitData.call(iapi);
        },
        action: 'click'
      };
      list.push(tempObj);
    }

    list.length > 0 && hamburgerMenu.appendInMenu(list);
  }

  getData (format) {
    // create a two dimensional array as given in the docs
    var iapi = this,
      dataObj = iapi.getCollatedData(),
      returnObj = [],
      datasets = dataObj.dataset,
      length = (datasets && datasets.length) || 0, // eslint-disable-line good-practices/no-single-usage-variable
      index = 0,
      dsInd = 0,
      setLen,
      set,
      j;

    // When a format is provided
    if (format) {
      // no transcoding needed for json
      if (/^json$/ig.test(format)) {
        returnObj = dataObj;
      } else if (/^csv$/ig.test(format)) {
        returnObj = iapi.getCSVString();
      } else {
        returnObj = global.core.transcodeData(dataObj,
          'json', format);
      }
    } else {
      // if no format has been specified, return data as 2d array.
      // while (length--) {
      for (; index < length; index += 1) {
        set = datasets[index];
        if (set) {
          set = datasets[index] && datasets[index].data;
          j = setLen = (set && set.length) || 0;
          j && (returnObj[dsInd] || (returnObj[dsInd] = [getValidValue(datasets[index].id,
            NULLSTR)]));
          while (j--) {
            returnObj[dsInd][j + 1] = getValidValue(set[j].id, NULLSTR);
          }

          setLen && (dsInd += 1);
        }
      }
    }
    return returnObj;
  }

  getCSVString () {
    var dataObj = this.getData(),
      i = dataObj.length;

    while (i--) {
      dataObj[i] = dataObj[i].join(COMMA);
    }

    return dataObj.join('|');
  }

  /**
   * Takes datasource and finds out all the points present in the selection box.
   * @returns {Object} origChartData - Final data
   * @memberof SelectScatter
   */
  getCollatedData (incomingJSON) {
    var api = this,
      dataset = this.getDatasets(), // eslint-disable-line good-practices/no-single-usage-variable
      selectedArr = api.config._selectEleArr,
      len = (selectedArr && selectedArr.length) || 0,
      jsonData = incomingJSON || api.getFromEnv('dataSource'),
      origChartData = extend2({}, jsonData),
      origDataSets = origChartData.dataset,
      xPos,
      yPos,
      oriDataArr,
      selectionBoxObj,
      lenDS = origDataSets && origDataSets.length,
      isSelected = false, // eslint-disable-line good-practices/no-single-usage-variable
      dataIndex,
      setObj,
      dataLen,
      startX,
      endX,
      startY,
      endY,
      selectedData = [];

    if (!len || !lenDS) {
      return jsonData;
    }
    while (len--) {
      selectionBoxObj = selectedArr[len];
      if (!selectionBoxObj) {
        continue;
      }
      startX = selectionBoxObj.startX;
      endX = selectionBoxObj.endX;
      startY = selectionBoxObj.startY;
      endY = selectionBoxObj.endY;
      dataIndex = lenDS;
      while (dataIndex--) {
        if (!dataset[dataIndex].getState('visible')) {
          continue;
        }
        selectedData[dataIndex] || (selectedData[dataIndex] = {
          data: []
        });
        oriDataArr = origDataSets[dataIndex].data;
        dataLen = oriDataArr && oriDataArr.length;
        while (dataLen--) {
          setObj = oriDataArr[dataLen];
          xPos = setObj.x;
          yPos = setObj.y;
          if (xPos > startX && xPos < endX &&
                          yPos < startY && yPos > endY) {
            selectedData[dataIndex].data[dataLen] = isSelected = true;
          }
        }
      }
    }

    while (lenDS--) {
      oriDataArr = origDataSets[lenDS].data;
      dataLen = oriDataArr && oriDataArr.length;
      while (dataLen--) {
        if (!(selectedData[lenDS] && selectedData[lenDS].data[dataLen])) {
          oriDataArr.splice(dataLen, 1);
        }
      }
    }
    return isSelected ? origChartData : jsonData;
  }

  createSelectionBox (event) {
    createSelectionBox.call(this, event);
  }

  _deleteAllSelection () {
    var chart = this,
      selectEleArr = chart.config._selectEleArr,
      selectEleObj,
      i,
      ii,
      selectEleItem,
      items1;

    if (selectEleArr) {
      for (i = 0, ii = selectEleArr.length; i < ii; i++) {
        selectEleObj = selectEleArr[i];
        for (items1 in selectEleObj) {
          if (selectEleObj.hasOwnProperty(items1)) {
            selectEleItem = selectEleObj[items1];
            selectEleItem.remove && selectEleItem.remove();
            delete selectEleObj[items1];
          }
        }
      }
      delete chart.config._selectEleArr;
    }
  }

  deleteSelection (ele, chart) {
    deleteSelection.call(this, ele, chart);
  }

  bindDragEvent (selectEleObj) {
    var chart = this,
      // logic = chart.logic,
      item;
    for (item in selectEleObj) {
      /Tracker/.test(item) && selectEleObj[item].drag(chart.move,
        chart.start, chart.up);
    }
  }

  start () {
    var ele = this,
      data = ele.data(configStr), // eslint-disable-line good-practices/no-single-usage-variable
      selectEleObj = data.selectEleObj,

      topT = selectEleObj.topTracker,
      rightT = selectEleObj.rightTracker,
      bottomT = selectEleObj.bottomTracker,
      leftT = selectEleObj.leftTracker,
      resizeT = selectEleObj.resizeTracker,

      topTData = topT.data(configStr),
      rightTData = rightT.data(configStr),
      bottomTData = bottomT.data(configStr),
      leftTData = leftT.data(configStr),
      resizeTData = resizeT.data(configStr),
      selectTData = selectEleObj.selectBoxTracker.data(configStr),
      bBox = selectEleObj.selectBoxTracker.getBBox();

    topTData.ox = bBox.x;
    topTData.oy = bBox.y;

    rightTData.ox = bBox.x2;
    rightTData.oy = bBox.y;

    bottomTData.ox = bBox.x;
    bottomTData.oy = bBox.y2;

    leftTData.ox = bBox.x;
    leftTData.oy = bBox.y;

    topTData.ox = bBox.x;
    topTData.oy = bBox.y;

    resizeTData.ox = bBox.x2;
    resizeTData.oy = bBox.y2;

    selectTData.ox = bBox.x;
    selectTData.oy = bBox.y;
    selectTData.ow = bBox.width;
    selectTData.oh = bBox.height;
    selectTData.ox2 = bBox.x2;
    selectTData.oy2 = bBox.y2;
    // on click take the selection box on top.
    selectEleObj.selectBoxG.toFront();

    topT.hide();
    rightT.hide();
    bottomT.hide();
    leftT.hide();
    resizeT.hide();
    ele.show();
  }

  move (evt) {
    var ele = this,
      data = ele.data(configStr),
      selectEleObj = data.selectEleObj,
      chart = data.chart,
      topT = selectEleObj.topTracker, // eslint-disable-line good-practices/no-single-usage-variable
      rightT = selectEleObj.rightTracker, // eslint-disable-line good-practices/no-single-usage-variable
      bottomT = selectEleObj.bottomTracker, // eslint-disable-line good-practices/no-single-usage-variable
      leftT = selectEleObj.leftTracker, // eslint-disable-line good-practices/no-single-usage-variable
      resizeT = selectEleObj.resizeTracker, // eslint-disable-line good-practices/no-single-usage-variable
      selectT = selectEleObj.selectBoxTracker,
      canvasLeft = selectEleObj.canvasLeft, // eslint-disable-line good-practices/no-single-usage-variable
      canvasRight = selectEleObj.canvasRight, // eslint-disable-line good-practices/no-single-usage-variable
      canvasTop = selectEleObj.canvasTop, // eslint-disable-line good-practices/no-single-usage-variable
      canvasBottom = selectEleObj.canvasBottom, // eslint-disable-line good-practices/no-single-usage-variable
      HALF_T_WID = -6,
      xAxis = chart.getChildren('xAxis')[0],
      yAxis = chart.getChildren('yAxis')[0],
      selectTData = selectT.data(configStr),
      attrib = {},
      bBox,
      dx = evt.data[0],
      dy = evt.data[1],
      eventArgs,
      x,
      y;

    dx = data.xChange ? dx : 0;
    dy = data.yChange ? dy : 0;

    x = dx + data.ox;
    y = dy + data.oy;

    x = Math.min(canvasRight - (data.ow || 0), Math.max(x, canvasLeft));
    y = Math.min(canvasBottom - (data.oh || 0), Math.max(y, canvasTop));

    switch (data.position) {
      case 1: // TOP
        attrib.y = Math.min(selectTData.oy2, y);
        attrib.height = Math.abs(selectTData.oy2 - y) || 1;
        topT.attr({
          y: y + HALF_T_WID
        });
        break;
      case 2: // Right
        attrib.x = Math.min(selectTData.ox, x);
        attrib.width = Math.abs(selectTData.ox - x) || 1;
        rightT.attr({
          x: x + HALF_T_WID
        });
        break;
      case 3: // Bottom
        attrib.y = Math.min(selectTData.oy, y);
        attrib.height = Math.abs(selectTData.oy - y) || 1;
        bottomT.attr({
          y: y + HALF_T_WID
        });
        break;
      case 4: // Left
        attrib.x = Math.min(selectTData.ox2, x);
        attrib.width = Math.abs(selectTData.ox2 - x) || 1;
        leftT.attr({
          x: x + HALF_T_WID
        });
        break;
      case 5: // Corner
        attrib.x = Math.min(selectTData.ox, x);
        attrib.width = Math.abs(selectTData.ox - x) || 1;
        attrib.y = Math.min(selectTData.oy, y);
        attrib.height = Math.abs(selectTData.oy - y) || 1;
        resizeT.attr({
          cx: x,
          cy: y
        });
        break;
      default:
        attrib.x = x;
        attrib.y = y;
        break;
    }

    if (!ele.data('dragStarted')) {
      bBox = selectT.getBBox();

      eventArgs = {
        selectionLeft: bBox.x,
        selectionTop: bBox.y,
        selectionWidth: bBox.width,
        selectionHeight: bBox.height,
        startXValue: xAxis.getValue(bBox.x),
        startYValue: yAxis.getValue(bBox.y),
        endXValue: xAxis.getValue(bBox.x + bBox.width),
        endYValue: yAxis.getValue(bBox.y + bBox.height),
        id: selectEleObj.id
      };

      chart.fireChartInstanceEvent('BeforeSelectionUpdate', eventArgs);
      ele.data('dragStarted', 1);
    }

    selectT.animate(attrib);

    if (selectEleObj.isVisible) {
      selectEleObj.closeButton.hide();
      selectEleObj.cornerInnerSymbol.hide();
      selectEleObj.cornerOuterSymbol.hide();
      selectEleObj.isVisible = false;
    }
  }

  up () {
    var ele = this,
      data = ele.data(configStr),
      selectEleObj = data.selectEleObj,
      chart = data.chart,
      xAxis = chart.getChildren('xAxis')[0],
      yAxis = chart.getChildren('yAxis')[0],
      topT = selectEleObj.topTracker,
      rightT = selectEleObj.rightTracker,
      bottomT = selectEleObj.bottomTracker,
      leftT = selectEleObj.leftTracker,
      resizeT = selectEleObj.resizeTracker,
      selectT = selectEleObj.selectBoxTracker, // eslint-disable-line good-practices/no-single-usage-variable
      RESIZE_T_RADIUS = 15,
      HALF_T_WID = -6,
      bBox,
      eventArgs;

    // using setTimeout to fix for the issue #RED-476.
    setTimeout(function () {
      bBox = selectT.getBBox();

      selectEleObj.startX = xAxis.getValue(bBox.x);
      selectEleObj.startY = yAxis.getValue(bBox.y);
      selectEleObj.endX = xAxis.getValue(bBox.x2);
      selectEleObj.endY = yAxis.getValue(bBox.y2);

      topT.attr({
        x: bBox.x,
        y: bBox.y + HALF_T_WID,
        width: bBox.width
      });
      rightT.attr({
        x: bBox.x2 + HALF_T_WID,
        y: bBox.y,
        height: bBox.height
      });
      bottomT.attr({
        x: bBox.x,
        y: bBox.y2 + HALF_T_WID,
        width: bBox.width
      });
      leftT.attr({
        x: bBox.x + HALF_T_WID,
        y: bBox.y,
        height: bBox.height
      });
      resizeT.attr({
        cx: bBox.x2,
        cy: bBox.y2
      });

      selectEleObj.closeButton.transform(t + bBox.x2 + COMMA + bBox.y);
      selectEleObj.cornerInnerSymbol.transform(t + bBox.x2 + COMMA + bBox.y2);
      selectEleObj.cornerOuterSymbol.transform(t + bBox.x2 + COMMA + bBox.y2);
      selectEleObj.closeButton.show();
      if (bBox.width < RESIZE_T_RADIUS || bBox.height < RESIZE_T_RADIUS) {
        selectEleObj.cornerInnerSymbol.hide();
        selectEleObj.cornerOuterSymbol.show();
      } else {
        selectEleObj.cornerInnerSymbol.show();
        selectEleObj.cornerOuterSymbol.hide();
      }
      selectEleObj.isVisible = true;

      topT.show();
      rightT.show();
      bottomT.show();
      leftT.show();
      resizeT.show();

      if (ele.data('dragStarted')) {
        eventArgs = {
          selectionLeft: bBox.x,
          selectionTop: bBox.y,
          selectionWidth: bBox.width,
          selectionHeight: bBox.height,
          startXValue: xAxis.getValue(bBox.x),
          startYValue: yAxis.getValue(bBox.y),
          endXValue: xAxis.getValue(bBox.x + bBox.width),
          endYValue: yAxis.getValue(bBox.y + bBox.height),
          data: chart.getCollatedData(),
          id: selectEleObj.id
        };
        chart.fireChartInstanceEvent('SelectionUpdated', eventArgs);
        ele.data('dragStarted', 0);
      }
    }, 100);
  }

  restoreData () {
    var chart = this,
      datasets = [],
      i;

    chart.iterateComponents((child) => {
      if ((child.getType && child.getType() === 'dataset')) {
        datasets.push(child);
      }
    });

    chart._deleteAllSelection();
    for (i = 0; i < datasets.length; i++) {
      datasets[i].asyncDraw();
    }
    chart.fireChartInstanceEvent('dataRestored', {});
    return true;
  }

  _postSpaceManagement () {
    super._postSpaceManagement();
    this._deleteAllSelection();
  }

  getDSdef () {
    return SelectScatterDataset;
  }
  // This method return the dataset-group definations for this charts
  getDSGroupdef () {
    return UNDEF;
  }
}

export default SelectScatter;
